/**
 * メール（/p/:projectId/:env/mail）: 同意したプレイヤーへのお知らせメールを書いて送る・予約する・結果を見る。
 * 根拠 docs/research/2026-09-27-mail-broadcast.md §5 D1〜D13・API は Core の確定した実装（mailApi.ts の冒頭）。
 *
 * 画面の順番 = 送るまでに要る順番:
 *   1. 使えない理由（kids のゲーム / features.mail が OFF）→ ここで止めて「ON にする方法」だけ出す
 *      WHY 止めるか: OFF のまま書く欄を見せると、書いてから送れないと分かる（書いた時間が無駄になる）
 *   2. 自動停止の帯（苦情・宛先不明が多い = 運営の確認まで送れない）
 *   3. 差出人情報（開発者単位・揃うまで送る操作を出さない）
 *   4. 本文を書く・宛先・送り方（今すぐ / 予約 / 定期）
 *   5. 送信の履歴と結果
 * メールのルートが無い古いサーバーでは、status の読み込みが route_missing になり「まだ使えない」を出す。
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { envPath, type FeaturesView, type GameConfig } from '../../api';
import { CodeBlock, ErrorState, fullDate, PageHeader, Spinner } from '../../components/ui';
import { enableCommands } from '../../featureToggle';
import { useResource } from '../../hooks';
import { translate, useT, type MessageKey } from '../../i18n';
import { mailPath, type MailSender, type MailStatus } from '../../mailApi';
import { mailBlocks, senderMissing, unwrap } from '../../mailLogic';
import { readWarmup } from '../../mailWarmup';
import { useShell } from '../ProjectShell';
import { ComposeCard } from './ComposeCard';
import { HistoryCard } from './HistoryCard';
import { SenderCard } from './SenderCard';
import { projectEyebrow } from '../../projectNav';

/** 自動停止の理由（サーバーの reason）→ 文言。知らない理由はそのまま出す（運営の手動停止などで増えうる） */
const REASON_KEYS: Record<string, MessageKey> = {
  complaint_rate: 'mail.suspended.reason.complaint_rate',
  bounce_rate: 'mail.suspended.reason.bounce_rate'
};
const reasonText = (r: string | null | undefined) => (r ? (REASON_KEYS[r] ? translate(REASON_KEYS[r]) : r) : translate('mail.suspended.reason.unknown'));

export function MailPage() {
  const { project, env } = useShell();
  const base = envPath(project.id, env);
  const mail = mailPath(project.id, env);
  const features = useResource<FeaturesView>(`${base}/features`);
  const cfg = useResource<{ version: number; config: GameConfig }>(`${base}/config`);
  // 状態だけは包み（{ status }）なしで返る（Core の確定した形）= unwrap を通さない
  const status = useResource<MailStatus>(`${mail}/status`);
  const sender = useResource<unknown>(`${mail}/sender`);
  // 送った・取り消したら履歴を読み直す合図（ComposeCard → HistoryCard）
  const [historyTick, setHistoryTick] = useState(0);
  const { t, tr } = useT();

  // undefined = 機能の一覧に mail がまだ無い（古いサーバー）。止めずに API の応答に任せる（mailLogic.mailBlocks）
  const featureEnabled = features.data?.features.find((f) => f.key === 'mail')?.enabled;
  const audience = typeof cfg.data?.config.audience === 'string' ? cfg.data.config.audience : undefined;
  const st = status.data ?? null;
  const snd = unwrap<MailSender>(sender.data, 'sender');
  // 差出人をまだ一度も保存していない = Core は `{ sender: null }` を返す（unwrap が null）。404 not_found は今の Core では
  // プロジェクトが見つからないときだけだが、「空」として扱っておく（差出人のカードが入力欄を出す = 失敗の帯より次の一手が分かる）
  const senderAbsent = sender.error?.status === 404 && !sender.error.notAvailable;
  const missing = sender.data || senderAbsent ? senderMissing(snd) : [];
  const blocks = mailBlocks({ audience, featureEnabled, state: st?.state, senderMissing: missing.length });
  // 送り始めの 1 日の上限（Core の mail-hardening）。期間外・古いサーバーは null = 帯も注意も出さない
  const warmup = readWarmup(st?.warmup);
  // 送った・取り消した: 履歴を読み直し、状態も読み直す（今日の残り・止めている時刻が変わる = 上限の帯を古い数のままにしない）
  const changed = () => {
    setHistoryTick((n) => n + 1);
    status.reload();
  };

  const header = (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('mail.title')} />
      <p className="page-lead muted">{t('mail.lead')}</p>
    </>
  );

  // 機能の ON/OFF と年齢層が分かるまでは何も出さない（OFF なのに書く欄が一瞬見える、を防ぐ）
  if ((features.loading && !features.data) || (cfg.loading && !cfg.data)) {
    return (
      <>
        {header}
        <Spinner />
      </>
    );
  }

  // kids のゲーム: 宛先は大人のプレイヤーだけ（D1）なので使えない。ON にする案内も出さない（サーバーが ON を断る）
  if (blocks.includes('kids')) {
    return (
      <>
        {header}
        <div className="alert alert-warn" role="note">
          <strong>{t('mail.kids.title')}</strong>
          <span>{tr('mail.kids.body', { key: <code>audience: "kids"</code> })}</span>
        </div>
      </>
    );
  }

  if (blocks.includes('feature_off')) {
    return (
      <>
        {header}
        <section className="card mail-off">
          <h2 className="card-title">{t('mail.off.title', { env: t(`env.inline.${env}`) })}</h2>
          <p>{t('mail.off.body')}</p>
          <ul className="mail-off-list">
            <li>{t('mail.off.point.consent')}</li>
            <li>{t('mail.off.point.adults')}</li>
            <li>{t('mail.off.point.sender')}</li>
          </ul>
          <p className="small muted">{tr('mail.off.how', { link: <Link to="../overview">{t('featureHint.link')}</Link> })}</p>
          <CodeBlock code={enableCommands(['mail'], env)} />
          <p className="small muted">{t('mail.off.kidsNote')}</p>
        </section>
      </>
    );
  }

  // サーバーにメールのルートがまだ無い = 「まだ使えない」を 1 回だけ出す（差出人・履歴のカードで同じ案内を 3 回並べない）
  if (status.error?.notAvailable) {
    return (
      <>
        {header}
        <ErrorState error={status.error} what={t('mail.title')} />
      </>
    );
  }

  return (
    <>
      {header}
      {status.error && <ErrorState error={status.error} what={t('mail.statusWhat')} onRetry={status.reload} />}
      {st?.state === 'suspended' && (
        <div className="alert alert-error mail-suspended" role="alert">
          <strong>{t('mail.suspended.title')}</strong>
          <span>{t('mail.suspended.reason', { reason: reasonText(st.reason) })}</span>
          {st.suspendedAt && <span className="small">{t('mail.suspended.since', { at: fullDate(st.suspendedAt) })}</span>}
          <span>{t('mail.suspended.body')}</span>
        </div>
      )}
      {audience === 'mixed' && (
        <p className="alert alert-info" role="note">
          {t('mail.mixedNote')}
        </p>
      )}

      <SenderCard mail={mail} resource={sender} absent={senderAbsent} sender={snd} missing={missing} />

      <ComposeCard
        mail={mail}
        gameName={project.name}
        sender={snd}
        sendingDomain={st?.sendingDomain ?? null}
        warmup={warmup}
        blocks={blocks}
        onSenderIncomplete={sender.reload}
        onChanged={changed}
      />

      <HistoryCard mail={mail} tick={historyTick} warmup={warmup} onChanged={changed} />
    </>
  );
}
